import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import flatt from "../../../assets/images/flatterwave.png";
import squad from "../../../assets/images/squad.png";
import { useAuth } from "../../../context/auth";
import useAxiosFunction from "../../../hooks/useAxiosFunction";
import { formatCurrency } from "../../../utils";
import InputField from "../../InputField/InputField";
import styles from "./styles.module.scss";

export type ProductNameTypes =
	| "Advance Payment"
	| "Average Debt Settlement Time"
	| "Backlog Settlement"
	| "Detailed Informal Report Of Transactions"
	| "Outstanding Salary Check"
	| "Trustworthiness Check";

export interface ProductsTypes {
	id: number;
	name: ProductNameTypes;
	price: number;
}

const paymentMethods = [
	{ id: "paystack", name: "Paystack", icon: flatt },
	{ id: "squad", name: "Squad", icon: squad },
];

interface PaymentModalProps {
	onClose: () => void;
	productName?: ProductNameTypes;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
	onClose,
	productName = "Advance Payment",
}) => {
	const { userData, refresh } = useAuth();
	const { axiosFetch } = useAxiosFunction();
	const [searchParams, setSearchParams] = useSearchParams();
	const modalRef = useRef<HTMLDivElement>(null);
	const [selectedMethod, setSelectedMethod] = useState<string>("squad");
	const [selectedProducts, setSelectedProducts] = useState<ProductsTypes>();
	const [createdBy] = useState(userData?.publicId);
	const [loading, setLoading] = useState(true);
	const [amount, setAmount] = useState<string>();
	const hasVerifiedPayment = useRef(false);

	const checkAmount = () => {
		if (productName !== "Advance Payment") return false;
		if (!amount || amount.length < 3) return true;
	};

	// Check for payment callback on mount
	useEffect(() => {
		const checkPaymentCallback = async () => {
			const trxref = searchParams.get("trxref");
			const reference = searchParams.get("reference");

			// Prevent multiple verification attempts
			if (
				trxref &&
				reference &&
				!hasVerifiedPayment.current &&
				userData?.publicId
			) {
				hasVerifiedPayment.current = true;

				// Get stored channel or default to paystack
				const storedChannel = sessionStorage.getItem("pending_payment_channel");
				const channel = storedChannel || "paystack";

				try {
					const res = await axiosFetch({
						method: "GET",
						url: `/Payments/VerifyPayment?userId=${userData.publicId}&paymentReferenceNumber=${reference}&channel=${channel}`,
						requestConfig: {
							headers: {
								Authorization: `Bearer ${userData.token}`,
							},
						},
					});

					if (res?.isSuccessful) {
						toast.success(res.remark || "Payment verified successfully!");
						refresh();

						// Clean up session storage
						sessionStorage.removeItem("pending_payment_channel");
						sessionStorage.removeItem("pending_payment_time");
					} else {
						toast.error(res?.message || "Payment verification failed");
					}
				} catch (error) {
					console.error("Error verifying payment from URL:", error);
					toast.error("Failed to verify payment. Please contact support.");
				}

				// Clean up URL parameters
				searchParams.delete("trxref");
				searchParams.delete("reference");
				setSearchParams(searchParams, { replace: true });
			}
		};

		checkPaymentCallback();
	}, [
		searchParams,
		setSearchParams,
		userData?.publicId,
		userData?.token,
		axiosFetch,
		refresh,
	]);

	// Clean up old pending payments
	useEffect(() => {
		const pendingTime = sessionStorage.getItem("pending_payment_time");
		if (pendingTime) {
			const elapsed = Date.now() - parseInt(pendingTime);
			// Clean up if older than 1 hour
			if (elapsed > 3600000) {
				sessionStorage.removeItem("pending_payment_channel");
				sessionStorage.removeItem("pending_payment_time");
			}
		}
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			const productRes = await axiosFetch({
				method: "GET",
				url: "/Products/GetProducts?pageNumber=1&pageSize=100",
			});

			const products: ProductsTypes[] = productRes?.productViewModel;

			if (products) {
				setSelectedProducts(
					products.filter((val) => val.name === productName)[0]
				);
			}

			setLoading(false);
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productName]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [onClose]);

	const handlePayment = async (channel: string) => {
		const paymentData = {
			amount:
				productName === "Advance Payment" ? amount : selectedProducts?.price,
			channel,
			paymentService: channel,
			productName,
			createdBy,
		};

		// Store payment channel and timestamp for verification after redirect
		sessionStorage.setItem("pending_payment_channel", channel);
		sessionStorage.setItem("pending_payment_time", Date.now().toString());

		try {
			toast.promise(
				axiosFetch({
					method: "POST",
					url: `/Payments/CreatePayment`,
					requestConfig: {
						data: paymentData,
						headers: {
							Authorization: `Bearer ${userData?.token}`,
						},
					},
				}),
				{
					loading: "Creating payment...",
					success: (res) => {
						if (res.isSuccessful) {
							onClose();
							refresh();

							// Open checkout URL - user will be redirected back with payment params
							if (res.checkoutUrl) {
								window.location.href = res.checkoutUrl; // Use location.href instead of window.open for proper redirect
							}

							return res.remark || "Redirecting to payment gateway...";
						}
						throw new Error(
							res.message || "An error occurred, please try again!"
						);
					},
					error: (err) => {
						// Clean up session storage on error
						sessionStorage.removeItem("pending_payment_channel");
						sessionStorage.removeItem("pending_payment_time");
						return err.message;
					},
				}
			);
		} catch (error) {
			console.error("Error initiating payment:", error);
			sessionStorage.removeItem("pending_payment_channel");
			sessionStorage.removeItem("pending_payment_time");
		}
	};

	// const VerifyPayment = async (channel: string, reference: string) => {
	// 	try {
	// 		toast.promise(
	// 			axiosFetch({
	// 				method: "GET",
	// 				url: `/Payments/VerifyPayment?userId=${userData?.publicId}&paymentReferenceNumber=${reference}&channel=${channel}`,
	// 				requestConfig: {
	// 					headers: {
	// 						Authorization: `Bearer ${userData?.token}`,
	// 					},
	// 				},
	// 			}),
	// 			{
	// 				loading: "Verifying payment...",
	// 				success: (res) => {
	// 					if (res.isSuccessful) {
	// 						refresh();
	// 						return res.remark || "Verification completed!";
	// 					}
	// 					throw new Error(
	// 						res.message || "An error occurred, please try again!"
	// 					);
	// 				},
	// 				error: (err) => err.message,
	// 			}
	// 		);
	// 	} catch (error) {
	// 		console.error("Error verifying payment:", error);
	// 	}
	// };

	const renderPaymentMethods = () =>
		paymentMethods.map((method) => (
			<div
				key={method.id}
				className={`${styles.paymentMethod} ${
					selectedMethod === method.id ? styles.selected : ""
				}`}
				onClick={() => setSelectedMethod(method.id)}
			>
				<div className={styles.content}>
					<div className={styles.icon}>
						<img src={method.icon} alt={method.name} />
					</div>

					<span>{method.name}</span>
				</div>

				<input
					title="method"
					type="radio"
					checked={selectedMethod === method.id}
					onChange={() => setSelectedMethod(method.id)}
				/>
			</div>
		));

	return (
		<div className={styles.overlay}>
			<div className={styles.modal} ref={modalRef}>
				<h6>Make Payment</h6>

				<p className={styles.sub}>
					Kindly make payment to continue this process.
				</p>

				{loading ? (
					<Spinner />
				) : (
					<>
						<div className={styles.paymentOptions}>
							{renderPaymentMethods()}
						</div>

						{selectedProducts?.name === "Advance Payment" ? (
							<div style={{ marginTop: "25px" }}>
								<InputField
									type="number"
									label="Enter an amount"
									name="amountInvolved"
									placeholder="Enter amount here"
									value={amount || 0}
									onChange={(_name, e) => {
										setAmount(Number(e.target.value.trim()).toFixed());
									}}
									error={
										checkAmount() && amount
											? "Enter an amount above ₦100"
											: undefined
									}
									isRequired
								/>
							</div>
						) : (
							<div className={styles.details}>
								<span>1 Request</span>

								<span>{formatCurrency(selectedProducts?.price || 0)}</span>
							</div>
						)}

						<button
							className="btn"
							onClick={() => handlePayment(selectedMethod)}
							disabled={checkAmount()}
						>
							Proceed with {selectedMethod}
						</button>
					</>
				)}
			</div>
		</div>
	);
};

export default PaymentModal;

export const Spinner = () => (
	<div className={styles.spinner}>
		<svg
			width="50"
			height="50"
			viewBox="0 0 50 50"
			xmlns="http://www.w3.org/2000/svg"
		>
			<circle
				cx="25"
				cy="25"
				r="20"
				strokeWidth="5"
				fill="none"
				strokeDasharray="100"
				strokeDashoffset="0"
			/>
		</svg>
	</div>
);
